import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';

@Component({
  standalone: true,
  selector: 'app-reset-password',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css'
})
export class ResetPasswordComponent {
  resetPasswordForm: FormGroup;
  isSuccess = false;
  private confirmationCode = '';
  private readonly recoverUrl = `${environment.wso2APIURL}DCAuthToken/Recovery/InitiatePasswordReset`;

  private passwordMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const password = group.get('newPassword')?.value;
    const confirm = group.get('confirmNewPassword');
    if (!confirm) {
      return null;
    }

    if (!password || !confirm.value) {
      if (confirm.errors?.['password_mismatch']) {
        const { password_mismatch, ...rest } = confirm.errors;
        confirm.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    if (password !== confirm.value) {
      confirm.setErrors({ ...(confirm.errors || {}), password_mismatch: true });
      return { password_mismatch: true };
    }

    if (confirm.errors?.['password_mismatch']) {
      const { password_mismatch, ...rest } = confirm.errors;
      confirm.setErrors(Object.keys(rest).length ? rest : null);
    }

    return null;
  };

  private passwordRulesValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    const pattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])(?!.*\s).{8,}$/;
    return pattern.test(value) ? null : { password_rules: true };
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private route: ActivatedRoute
  ) {
    this.resetPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, this.passwordRulesValidator]],
      confirmNewPassword: ['', Validators.required]
    }, { validators: [this.passwordMatchValidator] });

    this.confirmationCode = (this.route.snapshot.queryParamMap.get('confirmation') || '').trim();
    if (!this.confirmationCode) {
      this.backToLogin();
    }
  }


  isSubmitting: boolean = false;
  errorCodeMsg: string = '';
  submit() {
    if (!this.confirmationCode) {
      this.backToLogin();
      return;
    }
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }
    this.errorCodeMsg = '';
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    let requestPayload = {
      "confirmationCode": this.confirmationCode,
      "password": this.resetPasswordForm.value.newPassword
    }
    this.http.post(this.recoverUrl, requestPayload).subscribe({
      next: (response:any) => {
        if(response.message == 'Successful password reset.') {
          this.isSuccess = true;
        } else {
          if(response.message == "Not Accepted"){
            this.errorCodeMsg = 'RESET_PASSWORD_ERROR';
            return;
          }
          this.errorCodeMsg = response.code;
          this.isSuccess = false;
        }
      },
      error: () => {
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  backToLogin() {
    this.router.navigate(['/', RoutesPath.PUBLIC.LOGIN]);
  }
}
